import { useState } from 'react';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Download, Filter } from 'lucide-react';
import { useClientsConfig } from '@/config/clients-config';
import { ClientsSearchProps } from './types';

export function ClientsSearch({
  searchQuery,
  onSearchChange,
  onExport,
  enableExport = true,
  placeholder
}: ClientsSearchProps) {
  const config = useClientsConfig();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    if (!onExport) return;
    
    setIsExporting(true);
    try {
      await onExport(format);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex gap-4 items-center">
      {/* No debounce: the consumer (components/clients/Clients.tsx) filters client-side. */}
      {config.enableGlobalSearch && (
        <SearchInput
          placeholder={placeholder || config.searchPlaceholder}
          value={searchQuery}
          onChange={onSearchChange}
          className="flex-1"
        />
      )}

      {config.enableAdvancedFilters && (
        <Button
          variant="outline"
          size="icon"
          className="shrink-0"
          title="Advanced Filters"
        >
          <Filter className="h-4 w-4" />
        </Button>
      )}

      {enableExport && (config.enablePdfExport || config.enableExcelExport || config.enableCsvExport) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant={config.exportButtonVariant}
              disabled={isExporting}
              className="shrink-0"
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {config.enablePdfExport && (
              <DropdownMenuItem 
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                Export as PDF
              </DropdownMenuItem>
            )}
            {config.enableExcelExport && (
              <DropdownMenuItem 
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                Export as Excel
              </DropdownMenuItem>
            )}
            {config.enableCsvExport && (
              <DropdownMenuItem 
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                Export as CSV
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
